import type { QueryClient } from '@tanstack/react-query';
import type { Content } from '../../models/content/types';
import type { ContentFeedPage } from './use-content-feed-query';

/**
 * Updates all persisted React Query feed cache entries for a given content item.
 * Call this after any engagement action (like, bookmark) to keep localStorage in
 * sync, preventing stale state from overwriting Redux on the next page reload.
 */
export function updateFeedContentCache(
  queryClient: QueryClient,
  contentId: number,
  updates: Partial<Content>
): void {
  const updateItem = (item: Content): Content => {
    if (item.id !== contentId) return item;
    const { reaction_counts, ...rest } = updates;
    return {
      ...item,
      ...rest,
      // Deep-merge reaction_counts so other keys (views, shares, bookmarks) are preserved
      ...(reaction_counts !== undefined && {
        reaction_counts: { ...item.reaction_counts, ...reaction_counts },
      }),
    };
  };

  // contentFeed covers two shapes:
  //   - useContentFeedQuery (infinite): { pages: ContentFeedPage[], pageParams }
  //   - useContentFeedsBySectionsQuery (flat per-section): Content[]
  queryClient.setQueriesData<
    { pages: ContentFeedPage[]; pageParams: unknown[] } | Content[]
  >(['contentFeed'], (old) => {
    if (!old) return old;
    if (Array.isArray(old)) return old.map(updateItem);
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.map(updateItem),
      })),
    };
  });

  // homepageFeed: useQueries shape — flat Content[] per module
  queryClient.setQueriesData<Content[]>(['homepageFeed'], (old) => {
    if (!old) return old;
    return old.map(updateItem);
  });
}
